import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { DeleteConfirmationComponent } from '../../shared/modals/delete-confirmation/delete-confirmation.component';
import { TextBlockModalComponent } from '../../shared/modals/text-block-modal/text-block-modal.component';
import { ApiResponse, PaginatedResponse } from '../../shared/models/api.model';
import { DeleteConfirmationData, TextBlockModalData } from '../../shared/models/modal-data.model';
import { ITextBlock, TextBlockType } from '../../shared/models/text-block.model';
import { HelperService } from '../../shared/services/helper.service';
import { ModalService } from '../../shared/services/modal.service';
import { TextBlocksService } from '../../shared/services/text-blocks.service';
import { ToastService } from '../../shared/services/toast.service';


@Component({
  selector: 'app-text-blocks',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule, PaginationComponent],
  templateUrl: './text-blocks.component.html',
  styleUrls: ['./text-blocks.component.scss'],
})
export class TextBlocksComponent implements OnInit, OnDestroy {
  public textBlocks: ITextBlock[] = [];
  public isLoading = false;
  public searchTerm = '';

  public currentPage = 1;
  public pageSize = 10;
  public totalItems = 0;
  public totalPages = 1;

  public sortField = 'createdAt';
  public sortDirection = 'desc';

  private subscriptions = new Subscription();
  private searchTerms = new Subject<string>();

  constructor(
    private textBlocksService: TextBlocksService,
    private modalService: ModalService,
    private toastService: ToastService,
    public helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.loadTextBlocks();

    // Setup debounced search
    this.subscriptions.add(
      this.searchTerms.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(() => {
        // Reset to first page on search
        this.currentPage = 1;
        this.loadTextBlocks();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public loadTextBlocks(): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.textBlocksService.getAllTextBlocks(
        this.searchTerm,
        this.sortField,
        this.sortDirection,
        this.currentPage,
        this.pageSize
      ).subscribe({
        next: (response: ApiResponse<PaginatedResponse<ITextBlock>>) => {
          const paginatedData = response.DATA;
          this.textBlocks = paginatedData.data;
          this.totalItems = paginatedData.total;
          this.totalPages = paginatedData.totalPages;
          this.isLoading = false;
        },
        error: (error: Error) => {
          console.error('Error loading text blocks:', error);
          this.isLoading = false;
        }
      })
    );
  }

  public openCreateModal(): void {
    this.openTextBlockModal('create');
  }

  public openEditModal(textBlock: ITextBlock): void {
    this.openTextBlockModal('edit', textBlock);
  }

  public openDeleteModal(textBlock: ITextBlock): void {
    const data: DeleteConfirmationData = {
      title: 'Textbaustein löschen',
      message: 'Sind Sie sicher, dass Sie diesen Textbaustein löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'
    };

    this.modalService
      .open(DeleteConfirmationComponent, data)
      .then((confirmed: boolean) => {
        if (confirmed && textBlock._id) {
          this.handleDeleteTextBlock(textBlock._id);
        } else {
          this.modalService.close();
        }
      })
      .catch(() => { });
  }

  private openTextBlockModal(mode: 'create' | 'edit', textBlock?: ITextBlock): void {
    const data: TextBlockModalData = { mode, ...(textBlock && { textBlock }) };

    this.modalService
      .open(TextBlockModalComponent, data)
      .then((resultTextBlock: ITextBlock) => {
        if (resultTextBlock) {
          if (mode === 'create') {
            this.handleCreateTextBlock(resultTextBlock);
          } else if (mode === 'edit' && textBlock?._id) {
            this.handleUpdateTextBlock(textBlock._id, resultTextBlock);
          }
        }
        this.modalService.close();
      })
      .catch(() => { });
  }

  private handleCreateTextBlock(textBlock: ITextBlock): void {
    this.subscriptions.add(
      this.textBlocksService.createTextBlock(textBlock).subscribe({
        next: () => {
          this.toastService.showSuccess('Textbaustein erfolgreich erstellt');
          this.loadTextBlocks();
        },
        error: (error) => {
          console.error('Error creating text block:', error);
          this.toastService.showError('Fehler beim Erstellen des Textbausteins');
        },
      })
    );
  }

  private handleUpdateTextBlock(id: string, textBlock: ITextBlock): void {
    this.subscriptions.add(
      this.textBlocksService.updateTextBlock(id, textBlock).subscribe({
        next: () => {
          this.toastService.showSuccess('Textbaustein erfolgreich aktualisiert');
          this.loadTextBlocks();
        },
        error: (error) => {
          console.error('Error updating text block:', error);
          this.toastService.showError('Fehler beim Aktualisieren des Textbausteins');
        },
      })
    )
      ;
  }

  private handleDeleteTextBlock(id: string): void {
    this.subscriptions.add(
      this.textBlocksService.deleteTextBlock(id).subscribe({
        next: () => {
          this.toastService.showSuccess('Textbaustein erfolgreich gelöscht');
          this.modalService.close();
          this.loadTextBlocks();
        },
        error: (error) => {
          console.error('Error deleting text block:', error);
          this.toastService.showError('Fehler beim Löschen des Textbausteins');
          this.modalService.close();
        },
      })
    );
  }

  public onSearchChange(): void {
    this.searchTerms.next(this.searchTerm);
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadTextBlocks();
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTextBlocks();
  }

  public onSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadTextBlocks();
  }

  public getSortIcon(field: string): string {
    return this.helperService.getSortIcon(this.sortField, this.sortDirection, field);
  }

  public typeLabel(type: TextBlockType): string {
    switch (type) {
      case TextBlockType.LZ:
        return 'Lieferschein';
      case TextBlockType.ABS:
        return 'Auftragsbegleitschein';
      default:
        return type;
    }
  }

  public typeBadgeClass(type: TextBlockType): string {
    switch (type) {
      case TextBlockType.LZ:
        return 'bg-primary/15 text-primary';
      case TextBlockType.ABS:
        return 'bg-amber-400/15 text-amber-300';
      default:
        return 'bg-white/8 text-text-muted';
    }
  }
}
